import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  LifebuoyIcon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../../components/ui/Button';
import {
  SUPPORT_EMAILS,
  getSupportWhatsApp,
  buildWhatsAppUrl,
} from '../../config/support';

// Support / "Soporte" tab.
//
// Two direct contact routes for clients who hit a problem:
//  1) Report a problem by email — a small form (subject + description, with the
//     page they were on auto-noted as context) that composes a properly encoded
//     mailto: link addressed to BOTH verified team contacts. It opens the user's
//     own email client; nothing is sent by us and there is no ticket backend.
//  2) Chat with our team on WhatsApp — a wa.me deep link. This option only
//     renders once a verified business WhatsApp number is configured (see
//     config/support.js). Until then it is intentionally hidden so we never ship
//     a guessed number or a dead button in a customer flow.

// Build a mailto: link. Subject/body are URI-encoded; recipients are the
// verified support addresses joined by commas (all are copied on the email).
const buildMailto = (subject, body) => {
  const to = SUPPORT_EMAILS.join(',');
  const params = [];
  if (subject) params.push(`subject=${encodeURIComponent(subject)}`);
  if (body) params.push(`body=${encodeURIComponent(body)}`);
  const query = params.length ? `?${params.join('&')}` : '';
  return `mailto:${to}${query}`;
};

function SupportPage() {
  const { t } = useTranslation();
  const location = useLocation();

  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');

  // Only a safe pathname is used as context — never query strings, tokens, or
  // any customer record. The client is on a client route (e.g. "/campaigns").
  const contextPath = location?.pathname || '/';

  const whatsappNumber = getSupportWhatsApp();
  const whatsappUrl = useMemo(
    () => buildWhatsAppUrl(whatsappNumber, t('support.whatsapp.prefill')),
    [whatsappNumber, t]
  );

  const trimmedSubject = subject.trim();
  const trimmedDescription = description.trim();
  // Require a description before the mailto can be composed; keeps empty emails
  // from being opened and gives the client clear validation feedback.
  const canCompose = trimmedDescription.length > 0;

  const mailtoHref = useMemo(() => {
    if (!canCompose) return null;
    const subjectLine = trimmedSubject || t('support.email.defaultSubject');
    const body = `${trimmedDescription}\n\n---\n${t('support.email.contextLabel')}: ${contextPath}`;
    return buildMailto(subjectLine, body);
  }, [canCompose, trimmedSubject, trimmedDescription, contextPath, t]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!mailtoHref) return;
    // Open the user's email client. We never send on their behalf.
    window.location.href = mailtoHref;
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
      <header className="mb-8 border-b border-border pb-7">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400">
          <LifebuoyIcon className="h-5 w-5" aria-hidden="true" />
          {t('support.eyebrow')}
        </div>
        <h1 className="text-2xl font-bold leading-7 text-foreground sm:text-3xl">
          {t('support.title')}
        </h1>
        <p className="mt-3 max-w-3xl text-sm leading-6 sm:text-base" style={{ color: 'var(--muted)' }}>
          {t('support.description')}
        </p>
      </header>

      <div className="grid gap-6 md:grid-cols-2 md:items-start">
        {/* Email: report a problem */}
        <section
          aria-labelledby="support-email-heading"
          className="rounded-xl border border-border bg-card p-6"
        >
          <div className="mb-4 flex items-center gap-2">
            <EnvelopeIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" aria-hidden="true" />
            <h2 id="support-email-heading" className="text-lg font-semibold text-foreground">
              {t('support.email.title')}
            </h2>
          </div>
          <p className="mb-4 text-sm leading-6" style={{ color: 'var(--muted)' }}>
            {t('support.email.help')}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div>
              <label htmlFor="support-subject" className="mb-1.5 block text-sm font-medium text-foreground">
                {t('support.email.subjectLabel')}
              </label>
              <input
                id="support-subject"
                type="text"
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                placeholder={t('support.email.subjectPlaceholder')}
                className="input-field w-full"
                maxLength={150}
              />
            </div>

            <div>
              <label htmlFor="support-description" className="mb-1.5 block text-sm font-medium text-foreground">
                {t('support.email.descriptionLabel')}
                <span className="ml-1 text-red-500" aria-hidden="true">*</span>
              </label>
              <textarea
                id="support-description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder={t('support.email.descriptionPlaceholder')}
                className="input-field min-h-[8rem] w-full resize-y"
                required
                aria-required="true"
                maxLength={4000}
              />
            </div>

            <div className="rounded-lg bg-blue-50 px-3 py-2 text-xs leading-5 dark:bg-blue-500/10" style={{ color: 'var(--muted)' }}>
              <span className="font-medium">{t('support.email.contextLabel')}:</span>{' '}
              <code className="rounded bg-black/5 px-1 py-0.5 dark:bg-white/10">{contextPath}</code>
            </div>

            <p className="text-xs leading-5" style={{ color: 'var(--muted)' }}>
              {t('support.email.microcopy')}
            </p>

            <Button type="submit" disabled={!canCompose} className="w-full sm:w-auto">
              <EnvelopeIcon className="mr-2 h-4 w-4" aria-hidden="true" />
              {t('support.email.submit')}
            </Button>
          </form>

          <div className="mt-5 border-t border-border pt-4 text-sm" style={{ color: 'var(--muted)' }}>
            <p className="mb-1">{t('support.email.directLabel')}</p>
            <ul className="space-y-1">
              {SUPPORT_EMAILS.map((email) => (
                <li key={email}>
                  <a
                    href={`mailto:${email}`}
                    className="font-medium text-blue-600 underline decoration-blue-300 underline-offset-2 hover:text-blue-700 dark:text-blue-400"
                  >
                    {email}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* WhatsApp: chat with our team — only when a verified number is set */}
        <section
          aria-labelledby="support-whatsapp-heading"
          className="rounded-xl border border-border bg-card p-6"
        >
          <div className="mb-4 flex items-center gap-2">
            <ChatBubbleLeftRightIcon className="h-5 w-5 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
            <h2 id="support-whatsapp-heading" className="text-lg font-semibold text-foreground">
              {t('support.whatsapp.title')}
            </h2>
          </div>

          {whatsappUrl ? (
            <>
              <p className="mb-4 text-sm leading-6" style={{ color: 'var(--muted)' }}>
                {t('support.whatsapp.help')}
              </p>
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-10 items-center justify-center rounded-full bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2"
              >
                <ChatBubbleLeftRightIcon className="mr-2 h-4 w-4" aria-hidden="true" />
                {t('support.whatsapp.cta')}
              </a>
            </>
          ) : (
            <p className="text-sm leading-6" style={{ color: 'var(--muted)' }}>
              {t('support.whatsapp.comingSoon')}
            </p>
          )}
        </section>
      </div>
    </div>
  );
}

export default SupportPage;
